import { useContext, useEffect, useMemo, useState } from 'react';
import { AppBar, Box, Button, Tooltip, Typography } from '@mui/material';
import { CheckCircleOutline as CheckCircleOutlineIcon, OpenInNew as OpenInNewIcon } from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { ProfileInterface } from 'ts-interfaces';
import { BuyCreditsModal, isAdminRole, useCreditWorkspace, useWorkspaceContext, workspacesEnabled } from 'ui';
import { useTeamsProAccess } from '../hooks/useTeamsProAccess';
import { useMattoState } from '../MattoState';
import { DeepRenderResults } from '../deep_render/DeepRenderResults';
import { useBoards } from '../global-state/useBoards';
import { CreditBalanceDisplay } from '../components/credits/CreditBalanceDisplay';
import { CreditDetailsModal } from '../components/credits/CreditDetailsModal';
import Notifications from '../components/Notifications';
import { fetchData } from '../libs/util';
import { trackEvent } from '../libs/analyticsManager';
import { teamAnalytics } from '../team/teamAnalytics';
import UserSubscriptionModal from '../stripe/UserSubscriptionModal';
import AuthButton from '../studioxr/auth/AuthButton';
import { useLoginState } from '../studioxr/auth/LoginState';
import { WorkspaceSwitcher } from '../workspace/WorkspaceSwitcher';
import { AuthContext } from './Auth';
import { BarModeSwitch } from './BarModeSwitch';
import { FileMenu } from './FileMenu';
import { HelpButton } from './HelpButton';
import { getBarRouteState } from './barRouteState';

interface BarProps {
  toolTitle?: string;
}

const barAppBarSx = (theme: any) => ({
  boxShadow: 'none',
  display: 'flex',
  flexDirection: 'row',
  flexGrow: 0,
  flexShrink: 0,
  height: theme.bar.height,
  backgroundColor: theme.bar.backgroundColor,
  color: theme.bar.color,
  fontSize: theme.bar.fontSize,
  borderBottom: '1px solid #d0d0d0',
  position: 'static',
  zIndex: 10,
});

const barToolbarSx = {
  width: '100%',
  // Lets the bar shrink below its contents so the names inside can truncate.
  minWidth: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  paddingLeft: '16px',
  paddingRight: '8px',
};

const logoWrapperSx = {
  lineHeight: 'normal',
  '&:hover': { cursor: 'pointer' },
};


const upgradeButtonSx = (theme: any) => ({
  [theme.breakpoints.down('md')]: { display: 'none' },
  color: 'white',
  background: '#c58ed4',
  marginRight: '7px',
  maxHeight: theme.barButtons.maxHeight,
  fontWeight: 'bold',
  '& .MuiButton-startIcon svg': { color: 'white' },
  '&:hover': {
    backgroundColor: '#efefef',
    color: '#000',
    '& .MuiButton-startIcon svg': { color: '#000' },
  },
});

const blogButtonSx = {
  color: 'inherit',
  fontWeight: 500,
  textTransform: 'none' as const,
  fontSize: { xs: '0.8rem', sm: '0.875rem' },
  pl: { xs: 0.5, sm: 1 },
  pr: { xs: 1.5, sm: 2 },
  minWidth: 'auto',
  whiteSpace: 'nowrap' as const,
  '& .MuiButton-endIcon': { ml: 0.5 },
  '& .MuiButton-endIcon svg': { fontSize: '0.95rem' },
  '&:hover': {
    backgroundColor: 'transparent',
    textDecoration: 'underline',
    textUnderlineOffset: '3px',
  },
};

export const Bar = ({ toolTitle }: BarProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const routeState = useMemo(() => getBarRouteState(location.pathname), [location.pathname]);
  const { currentUser } = useContext(AuthContext);

  // Permissions come from LoginState — the source the create-team wizard
  // refreshes atomically. Feeding useWorkspaceContext from usePermissions'
  // module-cached copy (which the wizard does NOT refresh) made this
  // instance's reconciliation bounce a freshly-created 2nd team's workspace
  // back to Personal.
  const permissions = useLoginState((state) => state.permissions);

  // "Upgrade to Pro" is a personal-account action — hide it in a team
  // workspace. The only non-Pro user who'd see it there is a pure admin
  // (designers are Pro); they manage the team's plan under Billing, not via
  // a personal upgrade. It still shows in their personal workspace.
  const workspaceCtx = useWorkspaceContext(
    currentUser?.uid ?? null,
    permissions,
  );


  const profile = useLoginState((state) => state.profile);
  const setShowLogin = useLoginState((state) => state.setShowLogin);
  const setShowSubscription = useMattoState((state) => state.setShowSubscription);
  const activeConceptId = useMattoState((state) => state.activeConceptId);
  const activeConceptImageUrl = useMattoState((state) => state.activeConceptImageUrl);
  const conceptBusy = useMattoState((state) => state.conceptBusy);
  const resetBoards = useBoards((state) => state.resetBoards);

  const [showCreditDetails, setShowCreditDetails] = useState(false);
  const [showBuyCreditsModal, setShowBuyCreditsModal] = useState(false);

  // isPro: workspace-scoped capability (drives the Upgrade CTA / Pro badge).
  // isPersonalPro: personal entitlement, the personal branch of the
  // credit-purchase gate.
  // isTeamPro: capability Pro that is TEAM-derived (drives the "Pro Teams" badge
  // label). Unlike workspaceCtx.isTeam it is true even in gated production —
  // where the active workspace is forced to Personal but useCreditWorkspace pins
  // capability to the team — so a team designer reads "Pro Teams", not "Pro".
  const { isPro, isPersonalPro, isTeamPro, designFirm } = useTeamsProAccess();
  // Purchase gate keyed on the ACTIVE (credit) workspace, NOT membership-wide —
  // a team admin buys for the team only while acting in that team workspace;
  // in Personal it falls back to personal entitlement. Mirrors the modals.
  const creditWorkspace = useCreditWorkspace(currentUser?.uid ?? null, permissions);
  const isTeamWorkspace = creditWorkspace.kind === 'team';
  const isTeamAdmin = isAdminRole(
    creditWorkspace.kind === 'team' ? creditWorkspace.role : null,
  );
  const canPurchaseCredits = isTeamWorkspace ? isTeamAdmin : isPersonalPro;

  // Deep-link: ?modal=pro|plan|credits or ?upgrade=pro opens the appropriate modal (GTM emails, social).
  // modal=plan opens the upgrade modal on its plan picker (the /for-sketchup landing CTA).
  // modal=team opens the Create Team wizard (the /for-sketchup2 Team toggle); a personal Pro user can still create a team.
  useEffect(() => {
    if (!currentUser || !profile) return;
    const params = new URLSearchParams(location.search);
    const modal = params.get('modal') || (params.get('upgrade') === 'pro' ? 'pro' : null);
    if (!modal) return;

    if ((modal === 'pro' || modal === 'plan') && !isPro) {
      setShowSubscription(modal === 'plan' ? 'choosePlan' : 'startTrial');
      params.delete('modal');
      params.delete('upgrade');
      const search = params.toString();
      navigate(
        search ? { pathname: location.pathname, search: `?${search}` } : { pathname: location.pathname },
        { replace: true },
      );
    } else if (modal === 'team' && workspacesEnabled()) {
      teamAnalytics.createStarted('deep_link');
      setShowSubscription('createTeam');
      params.delete('modal');
      const search = params.toString();
      navigate(
        search ? { pathname: location.pathname, search: `?${search}` } : { pathname: location.pathname },
        { replace: true },
      );
    } else if (modal === 'credits' && canPurchaseCredits) {
      setShowBuyCreditsModal(true);
      params.delete('modal');
      params.delete('upgrade');
      const search = params.toString();
      navigate(
        search ? { pathname: location.pathname, search: `?${search}` } : { pathname: location.pathname },
        { replace: true },
      );
    }
  }, [location.search, location.pathname, currentUser, profile, canPurchaseCredits, navigate]);

  const confirmIfDirty = () => {
    if (!useMattoState.getState().isStateDirty) return true;
    return window.confirm('Board unsaved! Are you sure you want to leave?');
  };

  // In-app navigation away from a board with unsaved changes asks first.
  const confirmLeave = () => !routeState.isProjectRoute || confirmIfDirty();

  const handleHomePageClick = () => {
    if (!confirmLeave()) return;
    try {
      sessionStorage?.removeItem('redirectTo');
    } catch (error) {
      console.warn('[Bar] Unable to clear redirectTo before navigating home', error);
    }
    // Logged out: full load of / so the Cloudflare homepage Worker serves Webflow
    if (!currentUser) return void window.location.assign('/');
    // Home = the library
    navigate('/home/library');
  };

  const createNewBoard = () => {
    if (!confirmIfDirty()) return;
    if (!currentUser?.uid) return void setShowLogin('signup');
    window.location.assign('/board/new');
  };

  const handleTryMattoboardPro = () => {
    setShowSubscription('startTrial');
  };

  return (
    <AppBar sx={barAppBarSx}>
      <Box sx={barToolbarSx}>
        {/* minWidth 0 on both sides: when the bar is short of room, the
            workspace and credits-pill names truncate instead of pushing the
            account menu off-screen. */}
        <Box display="flex" alignItems="center" gap={1} sx={{ minWidth: 0 }}>
          <Tooltip title="Home">
            <Box sx={logoWrapperSx}>
              <img alt="Mattoboard" onClick={handleHomePageClick} src="/images/mattoboard-logo-new.svg" />
            </Box>
          </Tooltip>

          <WorkspaceSwitcher activeFirm={designFirm?.firm} onBeforeNavigate={confirmLeave} />

          {routeState.isProjectRoute && (
            <FileMenu createNewBoard={createNewBoard} currentUser={currentUser} />
          )}
        </Box>

        {/* <Box
          sx={{
            flex: 1,
            display: { xs: 'none', md: 'flex' },
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          {routeState.showModeButtons ? (
            <BarModeSwitch
              activeConceptId={activeConceptId}
              activeConceptImageUrl={activeConceptImageUrl}
              conceptBusy={conceptBusy}
              navigate={navigate}
              routeState={routeState}
            />
          ) : (
            toolTitle && <Typography sx={{ fontWeight: 600, fontSize: '0.9rem' }}>{toolTitle}</Typography>
          )}
        </Box> */}

        <Box display="flex" alignItems="center" gap={1} sx={{ minWidth: 0 }}>
          {currentUser &&
            profile &&
            !isPro &&
            !workspaceCtx.isTeam && (
              <Button
                id="mattoboardProBtn"
                onClick={handleTryMattoboardPro}
                startIcon={<CheckCircleOutlineIcon />}
                sx={upgradeButtonSx}
              >
                Upgrade to Pro
              </Button>
            )}

          <Button
            component="a"
            href="https://blog.mattoboard.com/"
            target="_blank"
            rel="noopener noreferrer"
            endIcon={<OpenInNewIcon />}
            sx={[
              blogButtonSx,
              // Signed in, the bar needs this room below md for the workspace
              // switcher, the credits pill and the account avatar.
              !!currentUser && { display: { xs: 'none', md: 'inline-flex' } },
            ]}
          >
            Blog
          </Button>

          {currentUser?.uid && (
            <CreditBalanceDisplay
              uid={currentUser.uid}
              onClick={() => setShowCreditDetails(true)}
              teamFirm={designFirm?.firm}
            />
          )}

          {currentUser && isPro && (
            <Box
              sx={{
                display: { xs: 'inline' },
                backgroundColor: '#B782FF',
                color: 'white',
                borderRadius: '16px',
                padding: '4px 8px',
                fontSize: '0.875rem',
                fontWeight: 'bold',
                marginLeft: '10px',
              }}
            >
              {isTeamPro ? 'Pro Teams' : 'Pro'}
            </Box>
          )}

          <Box display="flex" alignItems="center" gap={0}>
            <HelpButton profile={profile as ProfileInterface | null | undefined} />
            {!location.pathname.includes('/concepts2/') && <DeepRenderResults />}
            {currentUser?.uid && profile?.uid === currentUser.uid && <Notifications />}
          </Box>

          <AuthButton />
        </Box>
      </Box>

      <UserSubscriptionModal />
      <CreditDetailsModal
        open={showCreditDetails}
        onClose={() => setShowCreditDetails(false)}
        onBuyCredits={
          canPurchaseCredits
            ? () => {
                setShowCreditDetails(false);
                setShowBuyCreditsModal(true);
              }
            : undefined
        }
      />

      {canPurchaseCredits && (
        <BuyCreditsModal
          open={showBuyCreditsModal}
          onClose={() => setShowBuyCreditsModal(false)}
          uid={profile?.uid}
          fetchData={fetchData}
          trackEvent={trackEvent}
          userRole={profile?.userRole || ''}
          source="bar_credit_details"
        />
      )}
    </AppBar>
  );
};
